import React, {useState} from "react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient, useQuery} from '@tanstack/react-query';
import {Modal, ModalFooter, FormField, inputClass} from "./SettingsUI";
import AppleSelect from "../Common/AppleSelect";

const TableEditor = ({table, onClose, showNotification}) => {
    const [formData, setFormData] = useState({
        name: table ? table.name : '',
        status: table ? table.status : 'libre',
        capacity: table ? table.capacity : 4,
        area: table ? table.area?.['@id'] || table.area : '',
        shape: table ? table.shape : 'rectangle',
        icon: table ? table.icon : 'table',
        notes: table ? table.notes : ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const queryClient = useQueryClient();

    // Fetch areas for the dropdown
    const fetchAreas = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/areas', {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });
            return response.data || [];
        } catch (error) {
            return [];
        }
    };

    const {data: areas = []} = useQuery({
        queryKey: ['areas'],
        queryFn: fetchAreas,
    });

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Le nom de la table est requis";
        }

        if (!formData.capacity || formData.capacity < 1) {
            newErrors.capacity = "La capacité doit être au moins 1";
        }

        if (!formData.area) {
            newErrors.area = "La zone est requise";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const payload = {
                name: formData.name,
                status: formData.status,
                capacity: parseInt(formData.capacity),
                area: formData.area,
                shape: formData.shape,
                icon: formData.icon,
                notes: formData.notes
            };

            if (table) {
                // Update existing table
                await api.put(`/tables/${table.id}`, payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Table mise à jour avec succès');
            } else {
                // Create new table
                await api.post('/tables', payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Table créée avec succès');
            }

            queryClient.invalidateQueries({queryKey: ['tables']});
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={table ? 'Modifier la table' : 'Nouvelle table'}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} form="table-form" saving={submitting}
                                 label={table ? 'Enregistrer' : 'Créer'}/>}
        >
            <form id="table-form" onSubmit={handleSubmit} className="space-y-4">
                <FormField id="name" label="Nom" required error={errors.name}>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={inputClass(errors.name)}
                        placeholder="Ex: Table 1"
                        disabled={submitting}
                    />
                </FormField>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField id="area" label="Zone" required error={errors.area}>
                        <AppleSelect
                            id="area"
                            name="area"
                            value={formData.area}
                            onChange={handleChange}
                            error={errors.area}
                            disabled={submitting}
                            placeholder="Sélectionnez une zone"
                            options={areas.map((area) => ({value: `/api/areas/${area.id}`, label: area.name}))}
                        />
                    </FormField>

                    <FormField id="capacity" label="Capacité" required error={errors.capacity}>
                        <input
                            type="number"
                            id="capacity"
                            name="capacity"
                            value={formData.capacity}
                            onChange={handleChange}
                            min="1"
                            className={inputClass(errors.capacity)}
                            placeholder="4"
                            disabled={submitting}
                        />
                    </FormField>

                    <FormField id="status" label="Statut">
                        <AppleSelect
                            id="status"
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            disabled={submitting}
                            options={[
                                {value: 'libre', label: 'Libre'},
                                {value: 'occupee', label: 'Occupée'},
                                {value: 'reservee', label: 'Réservée'},
                            ]}
                        />
                    </FormField>

                    <FormField id="shape" label="Forme">
                        <AppleSelect
                            id="shape"
                            name="shape"
                            value={formData.shape}
                            onChange={handleChange}
                            disabled={submitting}
                            options={[
                                {value: 'rectangle', label: 'Rectangle'},
                                {value: 'circle', label: 'Cercle'},
                                {value: 'square', label: 'Carré'},
                            ]}
                        />
                    </FormField>
                </div>

                <FormField id="icon" label="Icône">
                    <input
                        type="text"
                        id="icon"
                        name="icon"
                        value={formData.icon}
                        onChange={handleChange}
                        className={inputClass(false)}
                        placeholder="table"
                        disabled={submitting}
                    />
                </FormField>

                <FormField id="notes" label="Notes">
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                        className={`${inputClass(false)} resize-none`}
                        placeholder="Notes supplémentaires"
                        disabled={submitting}
                    />
                </FormField>
            </form>
        </Modal>
    );
};

export default TableEditor;
